// src/pages/UsersListPage.js
import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { db } from '../../firebaseConfig';
import '../MuultiStyle.css';
import './UserListPage.css';

export default function UsersListPage() {
    const [users, setUsers]       = useState([]);
    const [loading, setLoading]   = useState(true);
    const [error, setError]       = useState('');
    const [viewMode, setViewMode] = useState('grid'); // plain JS
    const [searchTerm, setSearchTerm] = useState('');
    const [componentFilter, setComponentFilter] = useState('all'); // all | withComponents
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 14;

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const usersRef = collection(db, 'users');
                const q        = query(usersRef, where('muultiName', '!=', ''));
                const snap     = await getDocs(q);
                const list     = [];

                snap.forEach(doc => {
                    const d = doc.data();
                    if (
                        d.publicKey
                        && (d.muultiName || d.spaceName)
                        // && Array.isArray(d.components)
                        // && d.components.length > 0
                    ) {
                        list.push({
                            id: doc.id,
                            muultiName: d.muultiName,
                            profilePicUrl: d.profilePicUrl || '',
                            pageTitle: d.pageTitle,
                            pageDescription: d.pageDescription,
                            publicKey: d.publicKey,
                            headerBackground: d.headerBackground || null,
                            componentsCount: Array.isArray(d.components) ? d.components.length : 0
                        });
                    }
                });

                setUsers(list);
            } catch (e) {
                console.error(e);
                setError('Failed to load users.');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, componentFilter]);

    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filteredUsers = users.filter(user => {
        if (componentFilter === 'withComponents' && (user.componentsCount || 0) < 1) return false;
        if (!normalizedSearch) return true;
        const name = (user.muultiName || '').toLowerCase();
        const title = (user.pageTitle || '').toLowerCase();
        return name.includes(normalizedSearch) || title.includes(normalizedSearch);
    });

    const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    if (loading) return <div className="muulti-container"><p>Loading…</p></div>;
    if (error)   return <div className="muulti-container"><p className="error">{error}</p></div>;

    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * pageSize;
    const pagedUsers = filteredUsers.slice(startIndex, startIndex + pageSize);

    const tileStyleForUser = (hb) => {
        if (!hb || typeof hb !== 'object') return undefined;

        let base = {};
        if (hb.type === 'color') {
            base = { backgroundColor: hb.color || '#ffffff' };
        } else if (hb.type === 'gradient') {
            const colors = Array.isArray(hb.colors) && hb.colors.length >= 2
                ? hb.colors
                : ['#ffffff', '#000000'];
            const direction = hb.direction || 'to right';
            base = { backgroundImage: `linear-gradient(${direction}, ${colors[0]}, ${colors[1]})` };
        } else if (hb.type === 'image') {
            base = {
                backgroundColor: hb.fallback || '#ffffff',
                backgroundImage: hb.imageUrl ? `url(${hb.imageUrl})` : undefined,
                backgroundSize: hb.sizing || 'cover',
                backgroundPosition: hb.position || 'center',
                backgroundRepeat: hb.repeatMode || 'no-repeat'
            };
        }

        const style = { ...base };
        const hasBorder = hb.borderStyle || hb.borderWidth != null || hb.borderColor || hb.borderRadius != null;
        const hasBase = Object.keys(base).length > 0;

        if (hb.fontColor) style.color = hb.fontColor;
        if (hb.borderStyle) style.borderStyle = hb.borderStyle;
        if (hb.borderWidth != null) style.borderWidth = hb.borderWidth;
        if (hb.borderColor) style.borderColor = hb.borderColor;
        if (hb.borderRadius != null) style.borderRadius = hb.borderRadius;

        if (hasBase || hasBorder || hb.fontColor) {
            style.boxSizing = 'border-box';
            style.backgroundOrigin = 'border-box';
            style.backgroundClip = 'border-box';
            return style;
        }

        return undefined;
    };

    const paginationControls = filteredUsers.length > pageSize ? (
        <div className="users-directory-pagination">
            <button
                className="users-directory-page-btn"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={safePage === 1}
            >
                Prev
            </button>

            <div className="users-directory-page-info">
                Page {safePage} of {totalPages}
            </div>

            <button
                className="users-directory-page-btn"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
            >
                Next
            </button>
        </div>
    ) : null;

    return (
        <div className="muulti-container">
            <h1>Muulti Directory</h1>

            <div className="users-directory-toolbar">
                <div className="users-directory-search">
                    <input
                        className="users-directory-search-input"
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search by name..."
                        aria-label="Search users by name"
                    />
                    {searchTerm && (
                        <button
                            className="users-directory-clear"
                            onClick={() => setSearchTerm('')}
                            aria-label="Clear search"
                        >
                            Clear
                        </button>
                    )}
                </div>

                <div className="users-directory-filters" role="group" aria-label="Directory filters">
                    <label className="users-directory-filter-option">
                        <input
                            type="radio"
                            name="componentFilter"
                            value="all"
                            checked={componentFilter === 'all'}
                            onChange={() => setComponentFilter('all')}
                        />
                        All spaces
                    </label>
                    <label className="users-directory-filter-option">
                        <input
                            type="radio"
                            name="componentFilter"
                            value="withComponents"
                            checked={componentFilter === 'withComponents'}
                            onChange={() => setComponentFilter('withComponents')}
                        />
                        With components
                    </label>
                </div>

                <button
                    className="view-toggle-btn"
                    onClick={() => setViewMode(m => m === 'grid' ? 'list' : 'grid')}
                >
                    Switch to {viewMode === 'grid' ? 'List' : 'Grid'} View
                </button>
            </div>

            <div className="users-directory-meta">
                Showing {filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'}
            </div>

            {paginationControls}

            <div className={`users-directory-list ${viewMode}`}>
                {pagedUsers.map(user => {
                    const username = encodeURIComponent(user.muultiName || '');
                    const siteTitle = user.pageTitle?.trim() || user.muultiName;
                    const hasComponents = (user.componentsCount || 0) > 0;
                    const publicSpace2dHref = `/${username}`;
                    const publicSpace3dHref = `/3d/space/${username}`;

                    return (
                        <div
                            key={user.id}
                            className={`user-link ${viewMode}`}
                        >
                            <div
                                className={`user-directory-item ${viewMode}-view`}
                                style={tileStyleForUser(user.headerBackground)}
                            >
                                <div className={`user-directory-image-container${user.profilePicUrl ? '' : ' user-directory-image-container--placeholder'}`}>
                                    {user.profilePicUrl
                                        ? <img
                                            className="user-directory-image"
                                            src={user.profilePicUrl}
                                            alt={siteTitle}
                                        />
                                        : <div className="user-directory-placeholder">
                                            <h1>{siteTitle}</h1>
                                        </div>
                                    }
                                </div>
                                <div className="user-directory-description-container">
                                    {user.profilePicUrl && <h3>{siteTitle}</h3>}
                                    <div className="user-directory-username">{user.muultiName}</div>
                                </div>
                                <div className="user-directory-description">
                                    <p>{user.pageDescription}</p>
                                </div>
                                <div className="user-directory-actions">
                                    <Link to={publicSpace2dHref} className="user-directory-action-link">
                                        Visit MuultiSpace Page
                                    </Link>
                                    {hasComponents && (
                                        <Link to={publicSpace3dHref} className="user-directory-action-link">
                                            View 3D MuultiSpace
                                        </Link>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {!filteredUsers.length && (
                <div className="users-directory-empty">
                    {users.length ? 'No users match that search.' : 'No users found.'}
                </div>
            )}

            {paginationControls}
        </div>
    );
}
